import React, { useState, useMemo } from "react";
import styles from "./Components.module.scss";
import { NumericTextBox, Checkbox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label, Error } from "@progress/kendo-react-labels";
import { CiViewList } from "react-icons/ci";
import { useActiveBranches } from "services/branchService";

import UserBillingTargets from "../../BillingTargets/UserBillingTargets";
import UserTimeTargets from "../../TimeTargets/UserTimeTargets";
import { useActiveDepartments } from "services/departmentService";
import { useActiveWorkTypes } from "services/workTypeService";
import * as api from "api/index";
import { Dialog } from "@progress/kendo-react-dialogs";
import { getUserInfo, getUserById } from "services/userService";

const Days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ApplicationSettings = ({
  data = {},
  onChange,
  mode,
  errors = {},
  onRefreshUser,
}) => {
  const READ_ONLY = mode === "view";
  const { data: branchesList } = useActiveBranches();
  const { data: departments } = useActiveDepartments();
  const { data: workTypes } = useActiveWorkTypes();
  const [showTimeTargets, setShowTimeTargets] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const [timeTargetData, setTimeTargetData] = useState([]);
  const [billingTargetData, setBillingTargetData] = useState([]);

  const formattedBranches = useMemo(() => {
    const anyBranchOption = { branchId: null, branchName: "Any Branch" };
    return [anyBranchOption, ...(branchesList || [])];
  }, [branchesList]);

  const branchFinder = (id) => {
    const foundBranch = formattedBranches.find((item) => item.branchId === id);
    return foundBranch ? foundBranch.branchName : "Unknown Branch";
  };

  const openBillingTargets = async () => {
    try {
      const response = await api.getBillingTargets(data.userID);
      const finalList = [];
      let counter = 1;
      response?.data?.data?.forEach((item) => {
        const date = `${new Date(item.month).toLocaleString("default", {
          month: "long",
        })} ${new Date(item.month).toLocaleString("default", {
          year: "2-digit",
        })}`;
        item?.targetValues?.forEach((item_1) => {
          item_1.originalMonth = item.month;
          item_1.month = date;
          item_1.counter = counter;
          item_1.branchName = branchFinder(item_1.branchId);
          finalList.push(item_1);
          counter++;
        });
      });
      setBillingTargetData(finalList);
      setShowTargets(true);
    } catch (error) {}
  };

  const openTimeTargets = async () => {
    try {
      const response = await api.getTimeTargets(data.userID);
      const finalList = [];
      let counter = 1;

      const workingDaysArr = (data?.workingDays || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      workingDaysArr.forEach((item) => {
        let isAdded = false;
        const object = {
          weekDayName: item,
          value: 0,
          counter,
        };
        response?.data?.data?.forEach((item_1) => {
          if (item === item_1.weekDayName) {
            object.value = item_1.value;
            object.weekDayNo = item_1.weekDayNo;
            isAdded = true;
          }
        });
        if (!isAdded) {
          object.value = 0;
          object.weekDayNo = Days.indexOf(item);
        }
        counter++;
        finalList.push(object);
      });

      setTimeTargetData(finalList);
      setShowTimeTargets(true);
    } catch (error) {}
  };

  const refreshUserData = async () => {
    try {
      if (onRefreshUser) {
        await onRefreshUser();
      } else if (data.userID) {
        // Use getUserById to get fresh data from server instead of cached data
        const updatedUser = await getUserById(data.userID);
        if (updatedUser) {
          // Check if values actually changed before updating
          const timeTargetChanged = updatedUser.timeTarget !== data.timeTarget;
          const billingTargetChanged = updatedUser.billingTarget !== data.billingTarget;
          
          if (timeTargetChanged) {
            onChange("timeTarget", updatedUser.timeTarget);
          }
          if (billingTargetChanged) {
            onChange("billingTarget", updatedUser.billingTarget);
          }
        }
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    if (
      field === "branchId" ||
      field === "departmentId" ||
      field === "workTypeId"
    ) {
      if (typeof value === "object" && value !== null) {
        value = value.branchId || value.departmentID || value.workTypeID;
      }
      // Convert null/undefined/0 -> -1
      value = value || -1;
    }

    if (field === "workingDays") {
      value = parseInt(value) || 0;
    }
    onChange(field, value);
  };

  return (
    <>
      <div className={styles.container}>
        <h2 className={styles.title}>Application Settings</h2>

        <div className={styles.formGrid}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <Label>Time Target (Weekly Hours)</Label>
              <div className={styles.targetField}>
                <NumericTextBox
                  value={data.timeTarget ?? null}
                  onChange={(e) => handleFieldChange("timeTarget", e.value)}
                  format="n0"
                  className={styles.rightAlignInput}
                  placeholder="0"
                  disabled={READ_ONLY}
                  spinners={false}
                />

                <div
                  style={{
                    marginLeft: "-40px",
                    cursor: "pointer",
                    zIndex: "10",
                  }}
                >
                  <CiViewList
                    size={30}
                    color={mode === "add" ? "#B5B4C4" : "#241CCA"}
                    onClick={mode === "add" ? undefined : openTimeTargets}
                    style={{
                      cursor: mode === "add" ? "not-allowed" : "pointer",
                      opacity: data.isAddMode ? 0.5 : 1,
                    }}
                  />
                </div>
              </div>
              {errors.timeTarget && <Error>{errors.timeTarget}</Error>}
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <Label>Billing Target (Yearly Amount)</Label>
              <div className={styles.targetField}>
                <NumericTextBox
                  value={data.billingTarget ?? null}
                  onChange={(e) => handleFieldChange("billingTarget", e.value)}
                  format="n2"
                  className={styles.rightAlignInput}
                  placeholder="0.00"
                  disabled={READ_ONLY}
                  spinners={false}
                />
                <div
                  style={{
                    marginLeft: "-40px",
                    cursor: "pointer",
                    zIndex: "10",
                  }}
                >
                  <CiViewList
                    size={30}
                    color={mode === "add" ? "#B5B4C4" : "#241CCA"}
                    onClick={mode === "add" ? undefined : openBillingTargets}
                    style={{
                      cursor: mode === "add" ? "not-allowed" : "pointer",
                      opacity: data.isAddMode ? 0.5 : 1,
                    }}
                  />
                </div>
              </div>
              {errors.billingTarget && <Error>{errors.billingTarget}</Error>}
            </div>
          </div>

          <h3
            className={`${styles.title} m-0 fw-bold`}
            style={{ fontSize: "14px", marginBottom: 0 }}
          >
            User Defaults
          </h3>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <Label>Default Rate</Label>
              <NumericTextBox
                value={data.defaultRate ?? null}
                onChange={(e) => handleFieldChange("defaultRate", e.value)}
                className={styles.rightAlignNumber}
                format="n2"
                spinners={false}
                placeholder="0.00"
                disabled={READ_ONLY}
              />
              {errors.defaultRate && <Error>{errors.defaultRate}</Error>}
            </div>
            <div className={styles.formField}>
              <Label>Department</Label>
              <ComboBox
                value={
                  departments?.find(
                    (d) => d.departmentID === data.departmentId
                  ) || null
                }
                onChange={(e) => handleFieldChange("departmentId", e.value)}
                placeholder="Select department"
                disabled={READ_ONLY}
                data={departments || []}
                textField="name"
                dataItemKey="departmentID"
              />
              {errors.departmentId && <Error>{errors.departmentId}</Error>}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <Label>Branch</Label>
              <ComboBox
                value={formattedBranches.find(
                  (b) => b.branchId === data.branchId
                )}
                onChange={(e) => handleFieldChange("branchId", e.value)}
                placeholder="Select branch"
                disabled={READ_ONLY}
                data={formattedBranches}
                textField="branchName"
                dataItemKey="branchId"
              />
              {errors.branchId && <Error>{errors.branchId}</Error>}
            </div>
            <div className={styles.formField}>
              <Label>Work Type</Label>
              <ComboBox
                value={
                  workTypes?.find((w) => w.workTypeID === data.workTypeId) ||
                  null
                }
                onChange={(e) => handleFieldChange("workTypeId", e.value)}
                placeholder="Select work type"
                disabled={READ_ONLY}
                data={workTypes || []}
                textField="name"
                dataItemKey="workTypeID"
              />
              {errors.workTypeId && <Error>{errors.workTypeId}</Error>}
            </div>
          </div>

          <hr className={styles.horizontalLine} style={{ margin: "0" }} />

          <div className={styles.formRow}>
            <div className="d-flex flex-column gap-2">
              <Checkbox
                checked={!!data.isFeeEarner}
                onChange={(e) => handleFieldChange("isFeeEarner", e.value)}
                disabled={READ_ONLY}
                label="Is Fee Earner"
              />
              <Checkbox
                checked={!!data.active}
                onChange={(e) => handleFieldChange("active", e.value)}
                disabled={READ_ONLY}
                label="Is Active"
              />
            </div>
          </div>
        </div>
      </div>

      {showTargets && (
        <UserBillingTargets
          cancelEdit={() => setShowTargets(false)}
          item={data}
          data={billingTargetData}
          onRefresh={refreshUserData}
        />
      )}

      {showTimeTargets && (
        <UserTimeTargets
          cancelEdit={() => setShowTimeTargets(false)}
          item={data}
          data={timeTargetData}
          onRefresh={refreshUserData}
        />
      )}
    </>
  );
};

export default ApplicationSettings;
